import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/result.css";

const Result = ({ fileName, transcription, translation, status, error }) => {
  const navigate = useNavigate();
  const [audioUrl, setAudioUrl] = useState(null);
  const [activeTab, setActiveTab] = useState('transcription');
  const [selectedLanguage, setSelectedLanguage] = useState('es');
  const [currentTranslation, setCurrentTranslation] = useState('');

  // Available languages based on your API response
  const languages = {
    'de': 'German',
    'sw': 'Swahili', 
    'fr': 'French',
    'es': 'Spanish'
  };

  useEffect(() => {
    const storedUrl = localStorage.getItem("tempAudioUrl");
    if (storedUrl) {
      setAudioUrl(storedUrl);
    }

    // Set initial translation when component loads
    if (translation && selectedLanguage && translation[selectedLanguage]) {
      setCurrentTranslation(translation[selectedLanguage].text);
    }

    // Cleanup on refresh/navigation
    return () => {
      localStorage.removeItem("tempAudioUrl");
    };
  }, [translation, selectedLanguage]);

  const handleRetry = () => {
    navigate(0);
  };

  const handleLanguageChange = (languageCode) => {
    setSelectedLanguage(languageCode);
    if (translation && translation[languageCode]) {
      setCurrentTranslation(translation[languageCode].text);
    } else {
      setCurrentTranslation('Translation not available for this language.');
    }
  };

  const handleTabSwitch = (tab) => {
    setActiveTab(tab);
    if (tab === 'translation' && translation && selectedLanguage) {
      // Set the translation when switching to translation tab
      if (translation[selectedLanguage]) {
        setCurrentTranslation(translation[selectedLanguage].text);
      } else {
        setCurrentTranslation('Translation not available for this language.');
      }
    }
  };

  return (
    <div className="result-container">
      <h2 className="result-title">
        Transcripto <span className="highlight">Results</span>
      </h2>

      <p className="result-file">
        File name: <strong>{fileName || "Unknown"}</strong>
      </p>

      {status === "error" || !transcription ? (
        <div className="error-message">
          <p>{error || "Transcription failed."}</p>
          <div className="action-buttons">
            <button onClick={handleRetry} className="retry-btn danger">Try Again</button>
          </div>
        </div>
      ) : (
        <>
          {audioUrl && (
            <div className="audio-section">
              <h3 className="result-heading">Audio Playback</h3>
              <audio controls className="audio-player" src={audioUrl}></audio>
            </div>
          )}

          <div className="content-tabs">
            <div className="tabs-header">
              <button 
                className={`tab-button ${activeTab === 'transcription' ? 'active' : ''}`}
                onClick={() => handleTabSwitch('transcription')}
              >
                📝 Transcription
              </button>
              <button 
                className={`tab-button ${activeTab === 'translation' ? 'active' : ''}`}
                onClick={() => handleTabSwitch('translation')}
              >
                🌐 Translate
              </button>
            </div>

            <div className="tabs-content">
              <div className={`tab-content ${activeTab === 'transcription' ? 'active' : ''}`}>
                <div className="transcription-content">
                  {transcription}
                </div>
              </div>

              <div className={`tab-content ${activeTab === 'translation' ? 'active' : ''}`}>
                <div className="translation-section">
                  <div className="language-selector">
                    <label htmlFor="language-select">Translate transcription to:</label>
                    <select 
                      id="language-select"
                      className="language-dropdown"
                      value={selectedLanguage}
                      onChange={(e) => handleLanguageChange(e.target.value)}
                    >
                      {Object.entries(languages).map(([code, name]) => (
                        <option key={code} value={code}>{name}</option>
                      ))}
                    </select>
                  </div>

                  {currentTranslation ? (
                    <div className="translated-content">
                      <strong>{languages[selectedLanguage]} Translation:</strong>
                      <div style={{ marginTop: '12px' }}>{currentTranslation}</div>
                    </div>
                  ) : (
                    <div className="no-translation">
                      {translation ? 
                        "Select a language to see the translation." : 
                        "No translations available for this audio."
                      }
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="action-buttons">
            <button onClick={handleRetry} className="retry-btn">Transcribe Another Audio</button>
          </div>
        </>
      )}
    </div>
  );
};

export default Result;